import { useState, useEffect, useCallback, useRef } from 'react'
import SplashScreen from './components/SplashScreen'
import MainView from './components/MainView'
import { checkForUpdates, clearCachesAndReload } from './registerSW'
import { allImages } from './assets/images'

// All images that need to be preloaded (now using imported assets)
const PRELOAD_IMAGES = allImages

type AppView = 'splash' | 'main'

function preloadImages(urls: string[], timeoutMs = 5000): Promise<void> {
  return new Promise((resolve) => {
    // Set a timeout to ensure we don't wait forever
    const timeout = setTimeout(() => {
      console.log('Image preload timeout - continuing anyway')
      resolve()
    }, timeoutMs)

    Promise.all(
      urls.map(url => new Promise<void>((imgResolve) => {
        const img = new Image()
        img.onload = () => imgResolve()
        img.onerror = () => imgResolve() // Don't fail if image doesn't load
        img.src = url
      }))
    ).then(() => {
      clearTimeout(timeout)
      resolve()
    })
  })
}

export default function App() {
  const [currentView, setCurrentView] = useState<AppView>('splash')
  const [isUpdating, setIsUpdating] = useState(false)
  const [imagesLoaded, setImagesLoaded] = useState(true) // Start true to show UI immediately
  const lastHiddenTime = useRef<number>(0)

  // Preload all images on mount
  useEffect(() => {
    preloadImages(PRELOAD_IMAGES).then(() => {
      setImagesLoaded(true)
    })
  }, [])

  // Check for updates on app startup
  useEffect(() => {
    const performUpdateCheck = async () => {
      try {
        const { hasUpdate } = await checkForUpdates()

        if (hasUpdate) {
          setIsUpdating(true)
          console.log('Update available - refreshing caches...')
          await clearCachesAndReload()
        }
      } catch (error) {
        console.log('Update check failed, continuing normally')
      }
    }

    performUpdateCheck()
  }, [])

  // Handle app visibility changes (iOS app resume)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        lastHiddenTime.current = Date.now()
      } else if (document.visibilityState === 'visible') {
        // If app was hidden for more than 1 second, show splash again
        const hiddenDuration = Date.now() - lastHiddenTime.current
        if (lastHiddenTime.current > 0 && hiddenDuration > 1000) {
          setCurrentView('splash')
        }
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    // Also handle pageshow for bfcache scenarios
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        setCurrentView('splash')
      }
    }

    window.addEventListener('pageshow', handlePageShow)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('pageshow', handlePageShow)
    }
  }, [])

  const handleSplashComplete = useCallback(() => {
    if (!isUpdating && imagesLoaded) {
      setCurrentView('main')
    }
  }, [isUpdating, imagesLoaded])

  // Show splash screen while updating
  if (isUpdating) {
    return (
      <div style={styles.updatingContainer}>
        <SplashScreen onComplete={() => {}} />
        <div style={styles.updatingOverlay}>
          <div style={styles.updatingText}>Updating...</div>
        </div>
      </div>
    )
  }

  return (
    <div style={styles.app}>
      {/* Main View - always rendered and visible behind splash */}
      <div
        style={{
          ...styles.viewContainer,
          zIndex: 1
        }}
      >
        <MainView />
      </div>

      {/* Splash Screen - on top when active */}
      {currentView === 'splash' && (
        <div
          style={{
            ...styles.viewContainer,
            zIndex: 2
          }}
        >
          <SplashScreen onComplete={handleSplashComplete} />
        </div>
      )}
    </div>
  )
}

const styles: Record<string, React.CSSProperties> = {
  app: {
    width: '100%',
    height: '100%',
    position: 'relative',
    backgroundColor: '#000'
  },
  viewContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%'
  },
  updatingContainer: {
    width: '100%',
    height: '100%',
    position: 'relative'
  },
  updatingOverlay: {
    position: 'absolute',
    bottom: '20%',
    left: 0,
    right: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10
  },
  updatingText: {
    color: '#fff',
    fontSize: '18px',
    fontWeight: 'bold',
    textShadow: '0 2px 4px rgba(0,0,0,0.5)'
  }
}
